import parse from 'html-react-parser';
import FormItem from './FormItem';
import {
    Button,
    Checkbox,
    DatePicker,
    Form,
    Input,
    Radio,
    Select,
    Space,
} from 'antd';
import Multiple from './Multiple';
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import LastBtn from './preview/LastBtn';
import UploadCustom from './preview/UploadCustom';

export const QUESTION_TYPE = {
    SELECT: 'SELECT',
    EDITOR: 'EDITOR',
    RADIO: 'RADIO',
    CHECKBOX: 'CHECKBOX',
    DATE_TIME: 'DATE_TIME',
    DATE: 'DATE',
    SHORT: 'SHORT',
    LONG: 'LONG',
    UPLOAD: 'UPLOAD',
    TOPIC: 'TOPIC',
    OTP: 'OTP',
};

export const label = (label, style) => (
    <span
        style={{
            fontFamily: style?.question_fontFamily,
            fontSize: style?.question_fontSize,
        }}
    >
        {label ? parse(label) : ''}
    </span>
);

export const help = (help, style) =>
    help ? (
        <span
            style={{
                fontFamily: style?.description_fontFamily,
                fontSize: style?.description_fontSize,
            }}
        >
            {parse(help)}
        </span>
    ) : null;

export default function Question({ form }) {
    return (
        <FormItem
            isListField
            label={label(form.label, form.style)}
            name={form.name}
            initialValue={form.initialValue}
            rules={form.rules}
            editor={form.editor}
            help={help(form.help, form.style)}
        />
    );
}

Question.Select = ({ form }) => {
    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            help={help(form.help, form.style)}
        >
            <Select
                size="large"
                placeholder={form.placeholder}
                style={{ width: '100%' }}
                options={form.options}
                disabled={form.disabled}
            />
        </Form.Item>
    );
};

Question.Short = ({ form }) => {
    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            help={help(form.help, form.style)}
        >
            <Input
                disabled={form.disabled}
                size="large"
                placeholder={form.placeholder}
                maxLength={150}
                showCount
            />
        </Form.Item>
    );
};

Question.Long = ({ form }) => {
    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            help={help(form.help, form.style)}
        >
            <Input.TextArea
                size="large"
                placeholder={form.placeholder}
                maxLength={1000}
                showCount
                rows={4}
                disabled={form.disabled}
            />
        </Form.Item>
    );
};

Question.DateTime = ({ form, type }) => {
    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            valuePropName="date"
            help={help(form.help, form.style)}
        >
            <DatePicker
                showTime={type === QUESTION_TYPE.DATE_TIME}
                placeholder={
                    type === QUESTION_TYPE.DATE_TIME
                        ? 'วว/ดด/ปปปป ชม:นาที'
                        : 'วว/ดด/ปปปป'
                }
                className="w-80"
                disabled={form.disabled}
                size="large"
                format={
                    type === QUESTION_TYPE.DATE_TIME
                        ? 'DD/MM/YYYY HH:mm'
                        : 'DD/MM/YYYY'
                }
            />
        </Form.Item>
    );
};

Question.Upload = ({ form }) => {
    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            help={help(form.help, form.style)}
            className="flex"
        >
            <UploadCustom disabled={form.disabled} />
        </Form.Item>
    );
};

Question.Multiple = ({ form, item, part, type }) => {
    return <Multiple form={form} item={item} part={part} type={type} />;
};

Question.Otp = (form) => {
    return (
        <div className="font-semibold space-y-4 mt-4">
            <div>
                <strong>อีเมล</strong>
                <div>
                    <Space>
                        <Input
                            disabled
                            className="w-72"
                            size="large"
                            placeholder="ระบุอีเมลเพื่อรับ OTP"
                        />
                        <Button
                            disabled
                            type="primary"
                            size="large"
                            className="button-link"
                        >
                            รับรหัส
                        </Button>
                    </Space>
                </div>
            </div>
            <div>
                <strong>ป้อนรหัส OTP ที่ส่งไปยังอีเมล * </strong>
                <div>
                    <Space>
                        <Input
                            placeholder="ระบุรหัส OTP"
                            className="w-72"
                            disabled
                            size="large"
                        />
                        <Button
                            disabled
                            type="primary"
                            size="large"
                            className="button-link"
                        >
                            ยืนยัน OTP
                        </Button>
                    </Space>
                </div>
            </div>
            {help(form.help, form.style)}
        </div>
    );
};

Question.OtpWork = ({ form }) => {
    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={[
                {
                    required: form.rules[0].required,
                    message: 'กรุณายืนยัน OTP',
                },
            ]}
            help={help(form.help, form.style)}
        >
            <LastBtn form={form} />
        </Form.Item>
    );
};

Question.TOPIC = ({ form }) => {
    return (
        <Form.Item noStyle>
            <div className="font-semibold">{label(form.label, form.style)}</div>
            <div>{help(form.help, form.style)}</div>
        </Form.Item>
    );
};

const isOther = (label) => label === 'Other';

export const QuestionRadio = ({ form, options }) => {
    const item = form.item;

    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            help={help(form.help, form.style)}
        >
            <Radio.Group className="w-full">
                <Space direction="vertical" className="w-full">
                    {options?.map((option) => {
                        if (isOther(option?.label)) {
                            return (
                                <Fragment key={option?.value}>
                                    <Radio value={option?.value}>
                                        อื่นๆ (โปรดระบุ)
                                    </Radio>
                                    {item?.value?.includes('Other') && (
                                        <Form.Item
                                            name={[
                                                form.index.toString(),
                                                'other',
                                            ]}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'โปรดระบุ',
                                                },
                                            ]}
                                            className="mb-0 w-full"
                                        >
                                            <Input.TextArea
                                                rows={4}
                                                placeholder="โปรดระบุ"
                                                size="large"
                                                maxLength={1000}
                                                showCount
                                            />
                                        </Form.Item>
                                    )}
                                </Fragment>
                            );
                        }

                        return (
                            <Radio key={option?.value} value={option?.value}>
                                {option?.label}
                            </Radio>
                        );
                    })}
                </Space>
            </Radio.Group>
        </Form.Item>
    );
};

export const QuestionCheckbox = ({ form, options }) => {
    const item = form.item;

    return (
        <Form.Item
            label={label(form.label, form.style)}
            name={form.name}
            rules={form.rules}
            help={help(form.help, form.style)}
        >
            <Checkbox.Group className="w-full">
                <Space direction="vertical" className="w-full">
                    {options.map((option) => {
                        if (isOther(option?.label)) {
                            return (
                                <Fragment key={option?.value}>
                                    <Checkbox value={option?.value}>
                                        อื่นๆ (โปรดระบุ)
                                    </Checkbox>
                                    {item?.value?.includes('Other') && (
                                        <Form.Item
                                            name={[
                                                form.index.toString(),
                                                'other',
                                            ]}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'โปรดระบุ',
                                                },
                                            ]}
                                            className="mb-0 w-full"
                                        >
                                            <Input.TextArea
                                                rows={4}
                                                placeholder="โปรดระบุ"
                                                size="large"
                                                maxLength={1000}
                                                showCount
                                            />
                                        </Form.Item>
                                    )}
                                </Fragment>
                            );
                        }

                        return (
                            <Checkbox key={option?.value} value={option?.value}>
                                {option?.label}
                            </Checkbox>
                        );
                    })}
                </Space>
            </Checkbox.Group>
        </Form.Item>
    );
};

Question.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        initialValue: PropTypes.any,
        rules: PropTypes.array,
        editor: PropTypes.bool,
        help: PropTypes.string,
    }).isRequired,
};

Question.Select.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
        placeholder: PropTypes.string,
        options: PropTypes.array,
        disabled: PropTypes.bool,
    }).isRequired,
};

Question.Short.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
        placeholder: PropTypes.string,
        disabled: PropTypes.bool,
    }).isRequired,
};

Question.Long.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
        placeholder: PropTypes.string,
        disabled: PropTypes.bool,
    }).isRequired,
};

Question.DateTime.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
    }).isRequired,
    type: PropTypes.string.isRequired,
};

Question.Upload.propTypes = {
    form: PropTypes.shape({
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
        disabled: PropTypes.bool,
    }).isRequired,
};

Question.Multiple.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
    }).isRequired,
    item: PropTypes.object.isRequired,
    part: PropTypes.array.isRequired,
    type: PropTypes.string.isRequired,
};

Question.Otp.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        help: PropTypes.string,
    }).isRequired,
};

QuestionRadio.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
        item: PropTypes.object,
        index: PropTypes.number,
    }).isRequired,
    options: PropTypes.array.isRequired,
};

QuestionCheckbox.propTypes = {
    form: PropTypes.shape({
        label: PropTypes.string,
        style: PropTypes.object,
        name: PropTypes.string,
        rules: PropTypes.array,
        help: PropTypes.string,
        item: PropTypes.object,
        index: PropTypes.number,
    }).isRequired,
    options: PropTypes.array.isRequired,
};
